import { useState, useEffect } from "react";
import { devError } from "@/lib/logger";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { usePageTitle } from "@/hooks/usePageTitle";
import { gameService } from "@/services";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Zap, Bot, Trophy, Calendar, RefreshCw } from "lucide-react";
import DailyCountdown from "@/components/game/hud/DailyCountdown";
import { useActiveMode } from "@/hooks/useActiveMode";

export default function DailyChallengePage() {
  const { t } = useTranslation();
  usePageTitle("Daily Challenge");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [botCount, setBotCount] = useState(3);
  const [difficulty, setDifficulty] = useState("medium");
  // Same source-of-truth as the dashboard tile so the visual flavour
  // is consistent across both surfaces.
  const activeMode = useActiveMode();
  const [leaderboard, setLeaderboard] = useState(null);
  const [lbLoading, setLbLoading] = useState(false);

  const startChallenge = async () => {
    setLoading(true);
    try {
      const data = await gameService.createDailyChallenge({ botCount, difficulty });
      navigate(`/game/${data.game_id}?daily=true&bots=${data.bot_players.join(",")}`);
    } catch (e) {
      devError(e);
      // Axios interceptor already showed an error toast; just reset state.
    }
    setLoading(false);
  };

  const loadLeaderboard = async () => {
    setLbLoading(true);
    try {
      const data = await gameService.getDailyLeaderboard({ limit: 50 });
      setLeaderboard(data?.items || []);
    } catch (err) { devError("Failed to load daily leaderboard:", err); setLeaderboard([]); }
    setLbLoading(false);
  };

  // Auto-load leaderboard on mount (remove manual friction)
  useEffect(() => { loadLeaderboard(); }, []);

  return (
    <div className="min-h-screen bg-[#E3F2FD] dark:bg-[#0a1428] p-4 md:p-8" id="main-content" role="main" tabIndex={-1} data-testid="daily-challenge-page">
      <div className="max-w-lg mx-auto">
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mb-4 rounded-full" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" /> {t("dailyChallenge.navigation.back")}
        </Button>

        {/* Header */}
        <div className="text-center mb-6">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-3xl mb-3 shadow-lg"
            style={
              activeMode
                ? { background: `linear-gradient(135deg, ${activeMode.theme_color}EE, ${activeMode.theme_color}AA)` }
                : { background: "linear-gradient(135deg, rgb(168 85 247), rgb(79 70 229))" }
            }
          >
            <Calendar className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#1A237E] dark:text-[#E3F2FD]" style={{ fontFamily: "Fredoka" }}>
            {activeMode ? activeMode.name : t("dailyChallenge.title")}
          </h1>
          <p className="text-sm text-[#1A237E]/60 dark:text-[#E3F2FD]/60 mt-1">
            {activeMode ? activeMode.description : t("dailyChallenge.subtitle")}
          </p>
          <div className="mt-3 flex items-center justify-center gap-2 flex-wrap">
            <span
              className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider rounded-full px-2.5 py-1 border"
              data-testid="daily-urgency-badge"
              style={
                activeMode
                  ? {
                      backgroundColor: `${activeMode.theme_color}1A`,
                      color: activeMode.theme_color,
                      borderColor: `${activeMode.theme_color}33`,
                    }
                  : {
                      backgroundColor: "rgba(239, 68, 68, 0.1)",
                      color: "rgb(220, 38, 38)",
                      borderColor: "rgba(239, 68, 68, 0.2)",
                    }
              }
            >
              <span
                className="w-1.5 h-1.5 rounded-full animate-urgency-blink"
                style={{ background: activeMode ? activeMode.theme_color : "rgb(239 68 68)" }}
              />{" "}
              {activeMode ? t("liveMode.label") : t("dailyChallenge.urgencyBadge")}
            </span>
            <DailyCountdown variant="cool" testId="daily-countdown" />
          </div>
        </div>

        {/* Challenge Setup */}
        <div className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-6 mb-4">
          <div className="flex items-center gap-2 mb-4">
            <Bot className="w-5 h-5 text-[#38B6FF]" />
            <h2 className="text-lg font-bold text-[#1A237E] dark:text-[#E3F2FD]" style={{ fontFamily: "Fredoka" }}>{t("dailyChallenge.vsAi")}</h2>
          </div>

          <div className="mb-5">
            <label className="text-xs font-bold text-[#1A237E]/70 dark:text-[#E3F2FD]/70 mb-2 block uppercase tracking-wider">{t("dailyChallenge.opponents.label")}</label>
            <div className="grid grid-cols-3 gap-2">
              {[1, 2, 3].map(n => (
                <button key={n} type="button" data-testid={`bot-count-${n}`}
                  onClick={() => setBotCount(n)}
                  className={`rounded-2xl h-11 font-bold text-sm transition-all border-2 ${
                    botCount === n
                      ? "bg-[#38B6FF] text-white border-[#38B6FF] shadow-[0_3px_0_rgba(0,0,0,0.15)] scale-[1.02]"
                      : "bg-white/60 dark:bg-slate-900/60 text-[#1A237E]/70 dark:text-[#E3F2FD]/70 border-[#1A237E]/10 dark:border-[#E3F2FD]/10 hover:border-[#38B6FF]/40"
                  }`}>
                  {t("dailyChallenge.opponents.bot", { count: n })}
                </button>
              ))}
            </div>
          </div>

          <div className="mb-6">
            <label className="text-xs font-bold text-[#1A237E]/70 dark:text-[#E3F2FD]/70 mb-2 block uppercase tracking-wider">{t("dailyChallenge.difficulty.label")}</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: "easy", color: "bg-[#00E676] border-[#00E676]" },
                { id: "medium", color: "bg-[#38B6FF] border-[#38B6FF]" },
                { id: "hard", color: "bg-[#FF5A5F] border-[#FF5A5F]" },
              ].map(d => (
                <button key={d.id} type="button" data-testid={`difficulty-${d.id}`}
                  onClick={() => setDifficulty(d.id)}
                  className={`rounded-2xl h-11 font-bold text-sm transition-all border-2 ${
                    difficulty === d.id
                      ? `${d.color} text-white shadow-[0_3px_0_rgba(0,0,0,0.15)] scale-[1.02]`
                      : "bg-white/60 dark:bg-slate-900/60 text-[#1A237E]/70 dark:text-[#E3F2FD]/70 border-[#1A237E]/10 dark:border-[#E3F2FD]/10 hover:border-[#1A237E]/30 dark:border-[#E3F2FD]/30"
                  }`}>
                  {t(`dailyChallenge.difficulty.${d.id}`)}
                </button>
              ))}
            </div>
          </div>

          <Button data-testid="start-daily-btn" onClick={startChallenge} disabled={loading}
            className="w-full h-14 rounded-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white font-bold text-lg shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px] transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed animate-soft-pulse-ring-purple">
            <Zap className="w-5 h-5 mr-2" /> {loading ? t("dailyChallenge.button.starting") : t("dailyChallenge.button.start")}
          </Button>
          <div className="mt-3 flex items-center justify-center gap-3 text-[11px] font-semibold text-[#1A237E]/60 dark:text-[#E3F2FD]/60">
            <span className="inline-flex items-center gap-1" data-testid="daily-reward-hint">
              <Zap className="w-3 h-3 text-[#FFC107]" /> {t("dailyChallenge.reward.earn")}
            </span>
            <span className="text-[#1A237E]/20 dark:text-[#E3F2FD]/20">·</span>
            <span className="inline-flex items-center gap-1">
              <Trophy className="w-3 h-3 text-[#FFC107]" /> {t("dailyChallenge.reward.climb")}
            </span>
          </div>
        </div>

        {/* Daily Leaderboard */}
        <div className="bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-2 border-white/40 shadow-xl rounded-3xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-bold text-[#1A237E] dark:text-[#E3F2FD] flex items-center gap-2" style={{ fontFamily: "Fredoka" }}>
              <Trophy className="w-5 h-5 text-[#FFC107]" /> {t("dailyChallenge.leaderboard.title")}
            </h3>
            <Button variant="ghost" size="sm" onClick={loadLeaderboard} data-testid="load-daily-lb-btn" className="rounded-full h-8 w-8 p-0" disabled={lbLoading} title={t("dailyChallenge.refreshLabel")}>
              <RefreshCw className={`w-4 h-4 ${lbLoading ? "animate-spin" : ""}`} />
            </Button>
          </div>

          {/* Your rank inline — only shows when user already has an entry today */}
          {Array.isArray(leaderboard) && leaderboard.length > 0 && user?.name && (() => {
            const meIdx = leaderboard.findIndex(e => e.player_name === user.name);
            if (meIdx === -1) return null;
            const me = leaderboard[meIdx];
            const gapToTop = leaderboard[0].crypto_value - me.crypto_value;
            return (
              <div
                className="mb-3 flex items-center gap-3 rounded-2xl bg-[#38B6FF]/10 border border-[#38B6FF]/30 px-3 py-2"
                data-testid="daily-your-rank-chip"
              >
                <div className="w-9 h-9 rounded-full bg-[#38B6FF] text-white flex items-center justify-center font-bold text-sm shrink-0" style={{ fontFamily: "Fredoka" }}>
                  #{meIdx + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#1A237E]/60 dark:text-[#E3F2FD]/60 leading-none">{t("dailyChallenge.leaderboard.yourRank")}</p>
                  <p className="text-sm font-bold text-[#1A237E] dark:text-[#E3F2FD] truncate">${me.crypto_value}{" "}
                    <span className="text-[#1A237E]/50 dark:text-[#E3F2FD]/50 font-semibold text-xs">
                      {meIdx === 0 ? t("dailyChallenge.leaderboard.leader") : t("dailyChallenge.leaderboard.gapFromFirst", { gap: gapToTop })}
                    </span>
                  </p>
                </div>
              </div>
            );
          })()}

          {leaderboard === null || lbLoading ? (
            <p className="text-sm text-[#1A237E]/40 dark:text-[#E3F2FD]/40 text-center py-6 animate-pulse">{t("dailyChallenge.loadingRankings")}</p>
          ) : leaderboard.length === 0 ? (
            <p className="text-sm text-[#1A237E]/40 dark:text-[#E3F2FD]/40 text-center py-6">{t("dailyChallenge.empty")}</p>
          ) : (
            <div className="space-y-2">
              {leaderboard.map((entry, i) => {
                const isMe = user?.name && entry.player_name === user.name;
                return (
                  <div key={`daily-${entry.player_name || 'anon'}-${i}`}
                    className={`flex items-center justify-between rounded-xl px-4 py-3 transition-colors ${
                      i === 0
                        ? "bg-[#FFC107]/15 border-2 border-[#FFC107]/40"
                        : i === 1
                        ? "bg-gray-200/40 border-2 border-gray-300/40"
                        : i === 2
                        ? "bg-orange-200/20 border-2 border-orange-300/30"
                        : "bg-white/60 dark:bg-slate-900/60 border border-[#1A237E]/5 dark:border-[#E3F2FD]/5"
                    } ${isMe ? "ring-2 ring-[#38B6FF] ring-offset-1" : ""}`}
                    data-testid={`daily-lb-${i}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={`text-sm font-bold w-6 text-center ${i < 3 ? "text-[#1A237E] dark:text-[#E3F2FD]" : "text-[#1A237E]/40 dark:text-[#E3F2FD]/40"}`}>#{i + 1}</span>
                      <span className="text-sm font-semibold text-[#1A237E] dark:text-[#E3F2FD] truncate">{entry.player_name}</span>
                      {isMe && <span className="text-[10px] bg-[#38B6FF] text-white rounded-full px-2 py-0.5 font-bold shrink-0">{t("dailyChallenge.leaderboard.you")}</span>}
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-2">
                      <span className="text-sm font-bold text-[#00E676]">${entry.crypto_value}</span>
                      {entry.won && <Badge className="text-[10px] bg-green-100 text-green-700">{t("dailyChallenge.leaderboard.won")}</Badge>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
